import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Box,
  InputBase,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Divider,
  IconButton,
  Typography,
  Button,
  CircularProgress,
  Chip,
  Badge,
  Stack,
  Snackbar,
  Alert,
} from "@mui/material";
import { Search, History, Tune } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useClients } from "../hooks/useClients";
import { useConversations } from "../hooks/useConversations";
import debounce from "lodash.debounce";
import AdvancedSearchModal from "./AdvancedSearchModal";
import { fetchData } from "../api/client";
import { useOnboarding } from "../context/OnboardingContext";
import {
  getClientProfilePictureUrl,
  getInitials,
  getChannelIcon,
  getStatusChip,
} from "../utils/utils";
import { normalizeStatusInput } from "./StatusChipSelector";
import { useAuthContext } from "../context/AuthContext";
import { useTranslation } from "react-i18next";

const sanitizeText = (text) => {
  if (!text) return "";
  const div = document.createElement("div");
  div.innerHTML = text;
  return (div.textContent || div.innerText || "").trim();
};

const formatCurrencyBRL = (value) => {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return "R$ 0,00";
  return num.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

/** Extrai mensagem de erro da API (modelo fornecido) */
const extractApiError = (e, fallbackMsg) =>
  (e && e.response && e.response.data && e.response.data.message) ||
  e?.message ||
  fallbackMsg;

/** Chip de responsável com o mesmo padrão visual da lista de solicitações */
const renderAssigneeChip = (normalizedAssigned, userData, theme, status, tShell) => {
  const statusValue = normalizeStatusInput(status);
  const isClosed =
    statusValue === 2 || statusValue === 4 || statusValue === 5;
  const isQueue = !normalizedAssigned;
  const isMine =
    !!normalizedAssigned &&
    !!userData?.nome &&
    String(normalizedAssigned).trim() === String(userData.nome).trim();

  let bg;
  let color = theme.palette.common.white;
  let label;

  if (isQueue) {
    if (isClosed) {
      bg = theme.palette.error.light || "#ef9a9a";
      label = tShell("search.assigneeNoUser");
      color = theme.palette.getContrastText
        ? theme.palette.getContrastText(bg)
        : theme.palette.text.primary;
    } else {
      bg = theme.palette.grey[800];
      label = tShell("search.assigneeQueue");
    }
  } else if (isMine) {
    bg = theme.palette.success.main;
    label = tShell("search.assigneeMine");
  } else {
    bg = theme.palette.info.main;
    label = normalizedAssigned;
  }

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        backgroundColor: bg,
        color,
        fontSize: "0.75rem",
        height: "24px",
        maxWidth: "220px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    />
  );
};

/** Monta trilha de Tipo/Subtipos (tipo • subtipo1 • subtipo2 • subtipo3) */
const buildSaleTypePath = (sale) => {
  if (!sale) return "";
  const parts = [];

  const typeName = sale.typeName || sale.tipo_nome;
  const s1Name = sale.subtype1Name || sale.subtipo1_nome;
  const s2Name = sale.subtype2Name || sale.subtipo2_nome;
  const s3Name = sale.subtype3Name || sale.subtipo3_nome;

  if (typeName) parts.push(typeName);
  if (s1Name) parts.push(s1Name);
  if (s2Name) parts.push(s2Name);
  if (s3Name) parts.push(s3Name);

  if (parts.length) return parts.join(" • ");

  const ids = [];
  const typeId = sale.typeId ?? sale.id_tipo;
  const s1Id = sale.subtype1Id ?? sale.id_subtipo1;
  const s2Id = sale.subtype2Id ?? sale.id_subtipo2;
  const s3Id = sale.subtype3Id ?? sale.id_subtipo3;

  if (typeId != null) ids.push(`T${typeId}`);
  if (s1Id != null) ids.push(`S1-${s1Id}`);
  if (s2Id != null) ids.push(`S2-${s2Id}`);
  if (s3Id != null) ids.push(`S3-${s3Id}`);

  return ids.join(" • ");
};

const HELP_TOPICS = [
  {
    id: "help-automation-rules",
    title: "Como criar uma regra de automação?",
    keywords: ["automação", "automatizar", "regra", "regras", "fluxo automático"],
    route: "/settings/automation-rules",
    summary:
      "Use as Regras de Automação para disparar mensagens, atribuir responsáveis ou atualizar status de forma automática.",
  },
  {
    id: "help-connect-whatsapp",
    title: "Como conectar o canal WhatsApp?",
    keywords: ["whatsapp", "canal", "conectar whatsapp", "configurar whatsapp"],
    route: "/settings/channels",
    summary:
      "Vá em Canais nas Configurações para conectar ou revisar a integração com WhatsApp e outros canais.",
  },
  {
    id: "help-first-conversation",
    title: "Como atender minha primeira solicitação?",
    keywords: ["primeira solicitação", "primeiro atendimento", "atender cliente", "chat"],
    route: "/chat",
    summary:
      "Abra a tela de Chat para visualizar a fila de solicitações e iniciar o atendimento ao cliente.",
  },
  {
    id: "help-bpe",
    title: "Como usar o recurso BPe na solicitação?",
    keywords: ["bpe", "bilhete", "bilhete eletrônico", "rodoviário"],
    route: "/chat",
    summary:
      "Dentro de uma solicitação elegível, use o assistente BPe para consultar, emitir ou gerenciar bilhetes eletrônicos.",
  },
  {
    id: "help-pipeline",
    title: "O que é o Pipeline de Clientes?",
    keywords: ["pipeline", "etapas", "funil", "leads", "vendas"],
    route: "/pipeline",
    summary:
      "O Pipeline organiza seus leads e clientes por etapa do funil, ajudando a priorizar atendimentos e oportunidades.",
  },
];

const looksLikeQuestion = (text) => {
  const q = (text || "").toLowerCase().trim();
  if (!q) return false;
  if (q.includes("?")) return true;
  return (
    q.startsWith("como ") ||
    q.startsWith("o que ") ||
    q.startsWith("onde ") ||
    q.startsWith("por que") ||
    q.startsWith("porque ") ||
    q.startsWith("pra que") ||
    q.startsWith("para que") ||
    q.startsWith("duvida") ||
    q.startsWith("dúvida")
  );
};

const sortConversationsByQuery = (conversations, query) => {
  const term = (query || "").toLowerCase().trim();
  if (!term) return conversations;

  const isNumeric = /^\d+$/.test(term);

  const score = (c) => {
    const name = (c?.name || "").toLowerCase();
    const lastMessage = (c?.lastMessage || "").toLowerCase();
    const idStr = String(c?.id || "");

    if (isNumeric && idStr === term) return 0;
    if (name.startsWith(term)) return 1;
    if (name.includes(term)) return 2;
    if (lastMessage.includes(term)) return 5;
    return 10;
  };

  return [...conversations].sort((a, b) => {
    const sa = score(a);
    const sb = score(b);
    if (sa !== sb) return sa - sb;
    return 0;
  });
};

/* ================= Componente ================= */
const SearchAutocomplete = () => {
  const { t, i18n } = useTranslation("common");
  const { t: ts } = useTranslation("shell");
  const theme = useTheme();
  const dateLocale = i18n.language?.startsWith("en") ? "en-US" : "pt-BR";
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);

  const [searchHistory, setSearchHistory] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("searchHistory") || "[]");
    } catch {
      return [];
    }
  });

  const navigate = useNavigate();
  const autocompleteRef = useRef(null);

  const [startError, setStartError] = useState(null);
  const [startingByClient, setStartingByClient] = useState({});

  const { registerFeatureUse } = useOnboarding();
  const { userData } = useAuthContext();

  /** Data hooks */
  const normalizedTerm = (debouncedQuery || "").trim();
  const hasSearchTerm = normalizedTerm.length >= 2;

  // Queremos:
  // - quando o usuário abre o dropdown com termo vazio: carregar últimos resultados (como antes)
  // - quando está digitando: evitar tempestade de chamadas em 1 letra só
  const shouldQueryApi =
    showSuggestions && (normalizedTerm.length === 0 || hasSearchTerm);

  const { data: clientData, isLoading: loadingClients } = useClients(
    1,
    10,
    debouncedQuery,
    null,
    { enabled: shouldQueryApi }
  );

  const { data: conversationData, isLoading: loadingConversations } = useConversations(
    debouncedQuery,
    [],
    { default_date_window_days: 30 },
    false,
    { enabled: shouldQueryApi }
  );

  /** Vendas (pipeline) */
  const [salesData, setSalesData] = useState([]);
  const [loadingSales, setLoadingSales] = useState(false);

  /** Debounce input */
  const debounceSearch = useCallback(
    debounce((value) => setDebouncedQuery(value), 300),
    []
  );

  /** Handlers input */
  const handleInputChange = (e) => {
    const value = e.target.value || "";
    setQuery(value);
    setShowSuggestions(true);
    debounceSearch(value);
  };

  const handleSearch = () => {
    if (query.trim()) {
      const updatedHistory = [...new Set([query, ...(searchHistory || [])])].slice(0, 4);
      setSearchHistory(updatedHistory);
      sessionStorage.setItem("searchHistory", JSON.stringify(updatedHistory));
      registerFeatureUse("global-search");
      navigate(`/search-results?q=${encodeURIComponent(query)}`);
      setShowSuggestions(false);
    }
  };

  /** Fechar dropdown ao clicar fora */
  const handleClickOutside = (event) => {
    if (
      autocompleteRef.current &&
      !autocompleteRef.current.contains(event.target) &&
      event.target !== autocompleteRef.current.querySelector("input")
    ) {
      setShowSuggestions(false);
    }
  };

  const handleInputFocus = () => {
    setShowSuggestions(true);
    if (!query.trim()) debounceSearch("");
  };

  const matchedHelpTopics = useMemo(() => {
    const term = (debouncedQuery || "").toLowerCase().trim();
    if (!term) return [];

    const isQuestion = looksLikeQuestion(term);

    return HELP_TOPICS.filter((topic) => {
      const inTitle = topic.title.toLowerCase().includes(term);
      const inKeywords = (topic.keywords || []).some((k) =>
        k.toLowerCase().includes(term)
      );
      if (!inTitle && !inKeywords) return false;
      if (!isQuestion) {
        return inTitle || topic.keywords.some((k) =>
          term.split(/\s+/).some((word) => word && k.toLowerCase().includes(word))
        );
      }
      return true;
    }).slice(0, 3);
  }, [debouncedQuery]);

  /** Criar nova solicitação (RELACIONAMENTO) e abrir popup */
  const handleNewConversation = async (clientId) => {
    if (!clientId) return;

    setStartingByClient((prev) => ({ ...prev, [clientId]: true }));

    try {
      let projectId;
      try {
        const raw = sessionStorage.getItem("selectedCompanies");
        const arr = JSON.parse(raw || "[]");
        if (Array.isArray(arr) && arr.length > 0) projectId = Number(arr[0]);
      } catch {
        /* ignore */
      }

      const payload = {
        projectId: projectId || undefined,
        clientId,
        client: { id: clientId },
        typeId: undefined,
        typeKind: "1", // relacionamento
        initialMessage: "",
        source: "search-autocomplete",
      };

      const created = await fetchData("/conversations", "POST", payload);
      const conv = created?.data || created;
      if (!conv?.id) throw new Error("A API não retornou o ID da solicitação criada.");

      const width = window.screen.width;
      const height = window.screen.height;
      window.open(
        `/popup-call/${conv.id}`,
        "SolicitaçãoPopup",
        `width=${width},height=${height},top=0,left=0,scrollbars=no,toolbar=no,location=no,status=no,menubar=no`
      );
    } catch (error) {
      console.error("Erro ao criar nova solicitação:", error);
      setStartError(extractApiError(error, ts("search.startRequestError")));
    } finally {
      setStartingByClient((prev) => {
        const next = { ...prev };
        delete next[clientId];
        return next;
      });
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /** Buscar vendas (pipeline) sempre que o termo debounced mudar */
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const term = (debouncedQuery || "").trim();
      if (!term) {
        setSalesData([]);
        setLoadingSales(false);
        return;
      }

      setLoadingSales(true);
      try {
        let idsProjetosParam = "";
        try {
          const raw = sessionStorage.getItem("selectedCompanies");
          const arr = JSON.parse(raw || "[]");
          if (Array.isArray(arr) && arr.length > 0) {
            idsProjetosParam = arr.join(",");
          }
        } catch {
          /* ignore */
        }

        const params = new URLSearchParams();
        params.set("page", "1");
        params.set("limit", "5");
        params.set("search", term);
        if (idsProjetosParam) params.set("ids_projetos", idsProjetosParam);

        const resp = await fetchData(`/sales-pipeline/leads?${params.toString()}`, "GET");
        const list = resp?.data?.data || resp?.data || [];

        if (!cancelled) {
          setSalesData(Array.isArray(list) ? list : []);
        }
      } catch (err) {
        console.error("Erro ao carregar vendas do pipeline:", err);
        if (!cancelled) {
          setSalesData([]);
        }
      } finally {
        if (!cancelled) setLoadingSales(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  /** Distribuição e normalização dos resultados */
  const distributeResults = () => {
    const maxResults = 12;
    const maxHistory = 3;
    const maxConversations = 3;
    const maxClients = 2;
    const maxSales = 2;

    const filteredHistory = (searchHistory || []).filter((item) =>
      (item || "").toLowerCase().includes((query || "").toLowerCase())
    );

    const rawConversations = conversationData?.pages?.flatMap((page) => page?.list || []) || [];
    const allConversations = sortConversationsByQuery(rawConversations, debouncedQuery || query);
    const allClients = clientData?.clients || [];
    const allSales = salesData || [];
    const help = matchedHelpTopics;

    let remaining = maxResults;

    let history = filteredHistory.slice(0, Math.min(maxHistory, remaining));
    remaining -= history.length;

    let conversations = allConversations.slice(0, Math.min(maxConversations, remaining));
    remaining -= conversations.length;

    let clients = allClients.slice(0, Math.min(maxClients, remaining));
    remaining -= clients.length;

    let sales = allSales.slice(0, Math.min(maxSales, remaining));
    remaining -= sales.length;

    let helpResults = help.slice(0, Math.min(3, remaining));
    remaining -= helpResults.length;

    if (remaining > 0) {
      const addSales = allSales.slice(sales.length, sales.length + remaining);
      sales = sales.concat(addSales);
      remaining -= addSales.length;
    }
    if (remaining > 0) {
      const addConvs = allConversations.slice(conversations.length, conversations.length + remaining);
      conversations = conversations.concat(addConvs);
      remaining -= addConvs.length;
    }
    if (remaining > 0) {
      const addClients = allClients.slice(clients.length, clients.length + remaining);
      clients = clients.concat(addClients);
      remaining -= addClients.length;
    }
    if (remaining > 0) {
      const addHistory = filteredHistory.slice(history.length, history.length + remaining);
      history = history.concat(addHistory);
      remaining -= addHistory.length;
    }
    if (remaining > 0 && helpResults.length < help.length) {
      const addHelp = help.slice(helpResults.length, helpResults.length + remaining);
      helpResults = helpResults.concat(addHelp);
    }

    return { history, conversations, clients, sales, helpResults };
  };

  const { history, conversations, clients, sales, helpResults } = distributeResults();
  const isLoading = loadingClients || loadingConversations || loadingSales;
  const hasAnyData =
    history.length > 0 ||
    conversations.length > 0 ||
    clients.length > 0 ||
    sales.length > 0 ||
    helpResults.length > 0;
  const showInitialLoader = isLoading && !hasAnyData;

  /** Navegação por teclado nas listas */
  const totalItems =
    history.length +
    conversations.length +
    clients.length +
    sales.length +
    helpResults.length;

  const handleKeyDown = (e) => {
    if (totalItems === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % totalItems);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + totalItems) % totalItems);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (selectedIndex === -1) {
        handleSearch();
      } else {
        const h = history.length;
        const c = conversations.length;
        const cl = clients.length;
        const s = sales.length;

        if (selectedIndex < h) {
          navigate(`/search-results?q=${encodeURIComponent(history[selectedIndex])}`);
        } else if (selectedIndex < h + c) {
          const conversation = conversations[selectedIndex - h];
          navigate(`/chat/${conversation.id}`);
        } else if (selectedIndex < h + c + cl) {
          const client = clients[selectedIndex - h - c];
          navigate(`/clients/${client.id}`);
        } else if (selectedIndex < h + c + cl + s) {
          const sale = sales[selectedIndex - h - c - cl];
          navigate(`/sales-pipeline/${sale.id}`);
        } else {
          const helpIndex = selectedIndex - h - c - cl - s;
          const topic = helpResults[helpIndex];
          if (topic?.route) {
            navigate(topic.route);
          }
        }
        setShowSuggestions(false);
      }
    }
  };

  useEffect(() => {
    const total =
      history.length +
      conversations.length +
      clients.length +
      sales.length +
      helpResults.length;
    if (selectedIndex >= total) setSelectedIndex(-1);
  }, [
    history.length,
    conversations.length,
    clients.length,
    sales.length,
    helpResults.length,
    selectedIndex,
  ]);

  /** Datas seguras */
  const safeTimestamp = (conv) => {
    const raw =
      conv?.timestamp ||
      conv?.lastUpdate ||
      conv?.data_ultima_atualizacao ||
      null;
    if (!raw) return "—";
    const d = new Date(raw);
    return isNaN(d.getTime()) ? "—" : d.toLocaleDateString(dateLocale);
  };

  const safeSalesDate = (lead) => {
    const raw = lead?.createdAtLabel || lead?.createdAt || null;
    if (!raw) return "—";
    const d = new Date(raw);
    return isNaN(d.getTime()) ? "—" : d.toLocaleDateString(dateLocale);
  };

  return (
    <Box ref={autocompleteRef} sx={{ position: "relative", width: "100%" }}>
      {showAdvancedSearch && (
        <AdvancedSearchModal
          open={showAdvancedSearch}
          onClose={() => setShowAdvancedSearch(false)}
          onApply={(payload) => {
            const params = new URLSearchParams();
            if (payload.q) params.set("q", payload.q);
            if (payload.filters?.length) params.set("filters", payload.filters.join(","));
            if (payload.data_inicio) params.set("data_inicio", payload.data_inicio);
            if (payload.data_fim) params.set("data_fim", payload.data_fim);
            if (payload.date_field && payload.date_field !== "atualizacao") params.set("date_field", payload.date_field);
            if (payload.tipo_responsavel) params.set("tipo_responsavel", payload.tipo_responsavel);
            if (payload.ids_tipos?.length) params.set("ids_tipos", payload.ids_tipos.join(","));
            if (payload.ids_subtipo1?.length) params.set("ids_subtipo1", payload.ids_subtipo1.join(","));
            if (payload.ids_subtipo2?.length) params.set("ids_subtipo2", payload.ids_subtipo2.join(","));
            if (payload.ids_subtipo3?.length) params.set("ids_subtipo3", payload.ids_subtipo3.join(","));
            if (payload.ids_origens?.length) params.set("ids_origens", payload.ids_origens.join(","));
            if (payload.ids_usuarios?.length) params.set("ids_usuarios", payload.ids_usuarios.join(","));
            if (payload.include_message_text) params.set("include_message_text", "1");
            setShowAdvancedSearch(false);
            navigate(`/search-results?${params.toString()}`);
          }}
        />
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "8px",
          padding: "4px 12px",
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Search sx={{ marginRight: 1, color: theme.palette.text.secondary }} />
        <InputBase
          placeholder={t("searchPlaceholder")}
          fullWidth
          value={query}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          sx={{ fontSize: "16px", color: theme.palette.text.primary }}
        />
        <IconButton onClick={() => setShowAdvancedSearch(true)} size="small" aria-label={ts("search.advancedSearchAria")}>
          <Tune />
        </IconButton>
      </Box>

      {showSuggestions && (
        <Box
          sx={{
            position: "absolute",
            top: "48px",
            left: 0,
            right: 0,
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[4],
            borderRadius: "8px",
            zIndex: 10,
            maxHeight: "400px",
            overflowY: "auto",
          }}
        >
          {showInitialLoader ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {/* Sugestões de ajuda / dúvidas */}
              {helpResults.length > 0 && (
                <>
                  <Typography
                    variant="subtitle2"
                    sx={{ px: 2, pt: 1, color: theme.palette.primary.main, fontWeight: 600 }}
                  >
                    {ts("search.quickAnswers")}
                  </Typography>
                  {helpResults.map((topic, index) => {
                    const baseIndex =
                      history.length + conversations.length + clients.length + sales.length;
                    const thisIndex = baseIndex + index;

                    return (
                      <ListItem
                        key={`hhelp-${topic.id}`}
                        onClick={() => {
                          navigate(topic.route);
                          setShowSuggestions(false);
                        }}
                        selected={selectedIndex === thisIndex}
                        sx={{
                          cursor: "pointer",
                          alignItems: "flex-start",
                          backgroundColor:
                            selectedIndex === thisIndex
                              ? theme.palette.action.selected
                              : "transparent",
                          "&:hover": { backgroundColor: theme.palette.action.hover },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography variant="body2" fontWeight={600}>
                              {topic.title}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {topic.summary}
                            </Typography>
                          }
                        />
                      </ListItem>
                    );
                  })}
                  <Divider />
                </>
              )}

              {/* Histórico */}
              {history.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ px: 2, pt: 1, color: theme.palette.text.secondary }}>
                    {ts("search.searchHistory")}
                  </Typography>

                  {history.map((historyItem, index) => (
                    <ListItem
                      key={`h-${index}`}
                      onClick={() => {
                        setQuery(historyItem);
                        navigate(`/search-results?q=${encodeURIComponent(historyItem)}`);
                      }}
                      selected={selectedIndex === index}
                      sx={{
                        cursor: "pointer",
                        backgroundColor: selectedIndex === index ? theme.palette.action.selected : "transparent",
                        "&:hover": { backgroundColor: theme.palette.action.hover },
                      }}
                    >
                      <ListItemAvatar>
                        <IconButton size="small">
                          <History sx={{ color: theme.palette.text.secondary }} />
                        </IconButton>
                      </ListItemAvatar>
                      <ListItemText primary={historyItem} />
                    </ListItem>
                  ))}
                  <Divider />
                </>
              )}

              {/* Vendas (Pipeline) */}
              {sales.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ px: 2, pt: 1, color: theme.palette.text.secondary }}>
                    {ts("search.pipelineSales")}
                  </Typography>
                  {sales.map((sale, i) => {
                    const baseIndex = history.length + conversations.length + clients.length;
                    const thisIndex = baseIndex + i;

                    const total = formatCurrencyBRL(sale?.total);
                    const itemsLabel =
                      sale?.items && sale.items.length > 0
                        ? `${sale.items[0].name || ts("search.productFallback")}${
                            sale.items.length > 1 ? ` +${sale.items.length - 1}` : ""
                          }`
                        : "—";

                    const typePath = buildSaleTypePath(sale);
                    const statusLabel = sale?.statusLabel || "";
                    const statusValueForChip = sale?.status ?? sale?.statusCode ?? statusLabel;

                    return (
                      <ListItem
                        key={`s-${sale.id}`}
                        onClick={() => navigate(`/sales-pipeline/${sale.id}`)}
                        selected={selectedIndex === thisIndex}
                        sx={{
                          cursor: "pointer",
                          backgroundColor:
                            selectedIndex === thisIndex ? theme.palette.action.selected : "transparent",
                          "&:hover": { backgroundColor: theme.palette.action.hover },
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              backgroundColor: theme.palette.secondary.main,
                              color: theme.palette.secondary.contrastText,
                            }}
                          >
                            {(sale?.companyName || "V")?.[0]}
                          </Avatar>
                        </ListItemAvatar>

                        <Box flexGrow={1} overflow="hidden">
                          <Typography variant="subtitle2" noWrap>
                            {sale?.companyName || ts("search.clientFallback")}
                          </Typography>
                          <Typography variant="body2" color={theme.palette.text.secondary} noWrap>
                            {itemsLabel}
                          </Typography>
                          {typePath && (
                            <Typography variant="caption" color={theme.palette.text.secondary} noWrap>
                              {typePath}
                            </Typography>
                          )}
                          <Typography variant="body2" color={theme.palette.text.primary}>
                            {total}
                          </Typography>
                        </Box>

                        <Stack direction="column" alignItems="flex-end" spacing={0.5}>
                          <Typography variant="caption" color={theme.palette.text.secondary}>
                            {sale?.orderCode || `COD-${sale.id}`}
                          </Typography>
                          <Typography variant="caption" color="#aaa">
                            {safeSalesDate(sale)}
                          </Typography>
                          {(sale?.channelType || sale?.channelLabel) && (
                            <Box sx={{ mt: 0.25, display: "flex", alignItems: "center", gap: 0.5 }}>
                              {getChannelIcon(
                                sale?.channelLabel || sale?.channelType || "",
                                sale?.channelType || ""
                              )}
                              <Typography variant="caption" color={theme.palette.text.secondary} noWrap>
                                {sale?.channelLabel || sale?.channelType}
                              </Typography>
                            </Box>
                          )}
                          {statusValueForChip && getStatusChip(statusValueForChip, theme)}
                        </Stack>
                      </ListItem>
                    );
                  })}
                </>
              )}

              {/* Solicitações */}
              {conversations.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ px: 2, pt: 1, color: theme.palette.text.secondary }}>
                    {ts("search.requests")}
                  </Typography>
                  {conversations.map((conversation, i) => {
                    const baseIndex = history.length;
                    const thisIndex = baseIndex + i;

                    const unread = Number(conversation?.unread ?? 0);
                    const name = conversation?.name || "—";
                    const lastMsg = sanitizeText(conversation?.lastMessage || "");
                    const statusLabel = conversation?.statusLabel || conversation?.status || "";
                    const statusValueForChip =
                      conversation?.status != null && conversation?.status !== ""
                        ? conversation.status
                        : statusLabel;
                    const rawAssigned =
                      conversation?.assignedTo ?? conversation?.userName ?? null;
                    const normalizedAssigned =
                      !rawAssigned ||
                      rawAssigned === "Fila" ||
                      rawAssigned === "Queue"
                        ? null
                        : rawAssigned === "Minha Solicitação" || rawAssigned === "Me"
                        ? userData?.nome || rawAssigned
                        : rawAssigned;

                    return (
                      <ListItem
                        key={`c-${conversation?.id}`}
                        onClick={() => navigate(`/chat/${conversation?.id}`)}
                        selected={selectedIndex === thisIndex}
                        sx={{
                          cursor: "pointer",
                          backgroundColor:
                            selectedIndex === thisIndex ? theme.palette.action.selected : "transparent",
                          "&:hover": { backgroundColor: theme.palette.action.hover },
                        }}
                      >
                        <ListItemAvatar>
                          <Badge color="error" badgeContent={unread} overlap="circular" invisible={!unread}>
                            <Avatar>{name?.[0] || "?"}</Avatar>
                          </Badge>
                        </ListItemAvatar>

                        <Box flexGrow={1} overflow="hidden">
                          <Typography variant="subtitle2" noWrap>{name}</Typography>
                          <Typography variant="body2" color={theme.palette.text.secondary} noWrap>
                            {lastMsg || "—"}
                          </Typography>
                          {renderAssigneeChip(
                            normalizedAssigned,
                            userData,
                            theme,
                            statusValueForChip,
                            ts
                          )}
                        </Box>

                        <Stack direction="column" alignItems="flex-end" spacing={0.5}>
                          {(conversation?.channel ||
                            conversation?.channelLabel ||
                            conversation?.channelType ||
                            conversation?.channel_type) && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              {getChannelIcon(
                                conversation?.channel ?? conversation?.channelLabel ?? "",
                                conversation?.channelType ?? conversation?.channel_type ?? ""
                              )}
                              <Typography
                                variant="caption"
                                color={theme.palette.text.secondary}
                                noWrap
                              >
                                {conversation?.channel ??
                                  conversation?.channelLabel ??
                                  conversation?.channelType ??
                                  conversation?.channel_type}
                              </Typography>
                            </Box>
                          )}
                          <Typography variant="caption" color={theme.palette.text.primary}>
                            {ts("search.protocol", { id: conversation?.id })}
                          </Typography>
                          <Typography variant="caption" color="#aaa">
                            {safeTimestamp(conversation)}
                          </Typography>
                          {getStatusChip(statusValueForChip, theme)}
                        </Stack>
                      </ListItem>
                    );
                  })}
                  <Divider />
                </>
              )}

              {/* Clientes */}
              {clients.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ px: 2, pt: 1, color: theme.palette.text.secondary }}>
                    {ts("search.clients")}
                  </Typography>
                  {clients.map((client, i) => {
                    const baseIndex = history.length + conversations.length;
                    const thisIndex = baseIndex + i;
                    const isStarting = !!startingByClient[client?.id];

                    const avatarUrl = getClientProfilePictureUrl(
                      client?.imagem || client?.profile_picture
                    );

                    return (
                      <ListItem
                        key={`cl-${client?.id}`}
                        onClick={() => navigate(`/clients/${client?.id}`)}
                        selected={selectedIndex === thisIndex}
                        sx={{
                          cursor: "pointer",
                          backgroundColor:
                            selectedIndex === thisIndex ? theme.palette.action.selected : "transparent",
                          "&:hover": { backgroundColor: theme.palette.action.hover },
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            src={avatarUrl || undefined}
                            sx={{
                              backgroundColor: theme.palette.primary.main,
                              color: theme.palette.primary.contrastText,
                            }}
                          >
                            {!avatarUrl && getInitials(client?.nome)}
                          </Avatar>
                        </ListItemAvatar>

                        <ListItemText primary={client?.nome || "—"} secondary={client?.email || ""} />

                        <Button
                          variant="outlined"
                          size="small"
                          disabled={isStarting}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNewConversation(client?.id);
                          }}
                          sx={{
                            minWidth: 140,
                            color: theme.palette.primary.main,
                            borderColor: theme.palette.primary.light,
                            "&:hover": {
                              borderColor: theme.palette.primary.dark,
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                        >
                          {isStarting ? (
                            <Box display="flex" alignItems="center" gap={1}>
                              <CircularProgress size={16} />
                              {ts("search.starting")}
                            </Box>
                          ) : (
                            ts("search.newRequest")
                          )}
                        </Button>
                      </ListItem>
                    );
                  })}
                  <Divider />
                </>
              )}

              {history.length === 0 &&
                conversations.length === 0 &&
                clients.length === 0 &&
                sales.length === 0 &&
                helpResults.length === 0 && (
                  <Typography textAlign="center" sx={{ p: 2, color: theme.palette.text.disabled }}>
                    {ts("search.noResults")}
                  </Typography>
                )}
            </List>
          )}
        </Box>
      )}

      {/* Snackbar de erro ao iniciar solicitação */}
      <Snackbar
        open={!!startError}
        autoHideDuration={6000}
        onClose={(_, reason) => {
          if (reason === "clickaway") return;
          setStartError(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setStartError(null)} severity="error" variant="filled" sx={{ width: "100%" }}>
          {startError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SearchAutocomplete;